/**
 * Product Count Utilities
 * Consistente functies voor het afhandelen van product counts in de configurator
 */

/**
 * Haalt de huidige count van een product op
 * @param {Array} products - Array van producten
 * @param {number} productId - ID van het product
 * @returns {number} - Huidige count (0 als niet gevonden)
 */
export function getProductCount(products, productId) {
  if (!Array.isArray(products)) return 0;
  const product = products.find((p) => p.id === productId);
  return product?.count || 0;
}

/**
 * Controleert of een product al bestaat in de lijst
 * @param {Array} products - Array van producten
 * @param {number} productId - ID van het product
 * @returns {boolean} - True als het product bestaat
 */
export function productExists(products, productId) {
  if (!Array.isArray(products)) return false;
  return products.some((p) => p.id === productId);
}

/**
 * Verhoogt de count van een bestaand product of voegt een nieuw product toe
 * @param {Array} products - Array van producten
 * @param {Object} product - Product object om toe te voegen/verhogen
 * @returns {Array} - Nieuwe array met bijgewerkte producten
 */
export function increaseProductCount(products, product) {
  if (!Array.isArray(products)) return [{ ...product, count: 1 }];

  const existingIndex = products.findIndex((p) => p.id === product.id);

  if (existingIndex >= 0) {
    const currentCount = products[existingIndex].count || 0;
    const newProducts = [...products];

    if (currentCount === 0) {
      newProducts[existingIndex] = {
        ...newProducts[existingIndex],
        count: 1,
      };
    } else {
      newProducts[existingIndex] = {
        ...newProducts[existingIndex],
        count: currentCount + 1,
      };
    }

    return newProducts;
  } else {
    return [...products, { ...product, count: 1 }];
  }
}

/**
 * Verlaagt de count van een product of verwijdert het als count 0 wordt
 * @param {Array} products - Array van producten
 * @param {number} productId - ID van het product
 * @returns {Array} - Nieuwe array met bijgewerkte producten
 */
export function decreaseProductCount(products, productId) {
  if (!Array.isArray(products)) return [];

  const existingIndex = products.findIndex((p) => p.id === productId);

  if (existingIndex >= 0) {
    const currentCount = products[existingIndex].count || 0;

    if (currentCount > 1) {
      // Verlaag count met 1
      const newProducts = [...products];
      newProducts[existingIndex] = {
        ...newProducts[existingIndex],
        count: currentCount - 1,
      };
      return newProducts;
    } else {
      // Verwijder product als count 0 wordt
      // @Todo !! also remove product from personalization.productPersonalization and remove those functions from each remove
      return products.filter((p) => p.id !== productId);
    }
  }

  return products;
}

/**
 * Verwijdert een product volledig uit de lijst
 * @param {Array} products - Array van producten
 * @param {number} productId - ID van het product
 * @returns {Array} - Nieuwe array zonder het product
 */
export function removeProduct(products, productId) {
  if (!Array.isArray(products)) return [];
  return products.filter((p) => p.id !== productId);
}

/**
 * Verwijdert een product volledig uit de personalization
 * @param {Object} productPersonalizations - Array van personalization producten
 * @param {number} productId - ID van het product
 * @returns {Object} - Nieuwe array zonder het product
 */
export function removePersonalization(productPersonalizations, productId) {
  if (productPersonalizations && productPersonalizations[productId]) {
    delete productPersonalizations[productId];
  }
  return productPersonalizations;
}

/**
 * Initialiseert een product met de juiste count
 * @param {Object} product - Product object
 * @param {number} initialCount - Initiële count (default: 0)
 * @returns {Object} - Product met count property
 */
export function initializeProduct(product, initialCount = 0) {
  return {
    ...product,
    count: initialCount,
  };
}

/**
 * Berekent de totale prijs van een lijst producten
 * @param {Array} products - Array van producten
 * @returns {number} - Totale prijs
 */
export function calculateTotalPrice(products) {
  if (!Array.isArray(products)) return 0;

  return products.reduce((total, product) => {
    const price = product.price || 0;
    const count = product.count || 0;
    return total + price * count;
  }, 0);
}

/**
 * Berekent het totaal aantal producten van een lijst producten
 * @param {Array} products - Array van producten
 * @returns {number} - Totaal aantal producten
 */
export function calculateTotalQuantity(products) {
  if (!Array.isArray(products)) return 0;

  return products.reduce((total, product) => {
    const count = product.count || 0;
    return total + count;
  }, 0);
}

export const cleanupUrlParameter = (parameterName) => {
  const url = new URL(window.location);
  url.searchParams.delete(parameterName);

  if (url.searchParams.toString()) {
    history.replaceState({}, '', url.toString());
  } else {
    history.replaceState({}, '', url.pathname);
  }
};

export const getUrlParameter = (parameterName) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(parameterName);
};

export const decodeHtml = (str = '') => {
  if (!str || typeof str !== 'string') return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};

export const hasValidImage = (item) => {
  if (!item) return false;

  const imageUrl = item.imageUrl || item.image;
  if (!imageUrl || typeof imageUrl !== 'string') return false;

  const trimmedUrl = imageUrl.trim();
  if (trimmedUrl === '' || trimmedUrl === 'false' || trimmedUrl === 'null')
    return false;

  return true;
};
